import { readCoverageBetaTiming } from "@/components/coverage-beta/CoverageBetaActivityTracker";
import { useCoverageBetaNextTaskPrefetch } from "@/hooks/useCoverageBetaNextTaskPrefetch";
import {
  isEmptyAreaSubmittable,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { buildCoverageBetaResult } from "@/utils/build-coverage-beta-result";
import { isTaskAlreadyFinalized } from "@/utils/task-already-finalized";
import { CompleteTaskPayloadSchema } from "@/utils/zod-schemas/complete-task-payload-schema";
import { mvpApiCompleteJobTask } from "@labeling-tools/api/lebron/generated";
import { mvpApiGetNextPendingJobTaskOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { JobTaskDetailSchema } from "@labeling-tools/api/lebron/generated/types.gen";
import { useFetchJob } from "@labeling-tools/api/lebron/react-query-hooks";
import { Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { values } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";

interface Props {
  taskDetailResp: JobTaskDetailSchema | undefined;
}

export const CoverageBetaSubmitBtn = (props: Props) => {
  const { taskDetailResp } = props;
  const params = useParams<{ jobId: string }>();
  const job_id = params?.jobId ?? "";
  const task_id = taskDetailResp?.id ?? "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { data: job } = useFetchJob(job_id);
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);

  // Warm the next pending task (detail + parcel + images) while the labeler
  // works, so Submit can transition without a cold load.
  const { nextTaskId, nextTaskDetail, isPrefetchReady } =
    useCoverageBetaNextTaskPrefetch({
      jobId: job_id,
      currentTaskId: task_id,
      isCurrentTaskBenchmark:
        (taskDetailResp as any)?.type === "COVERAGE_REVIEW_BENCHMARK",
      enabled: !!job_id && !!task_id && !isReadOnly,
    });

  const enableSubmit = useCoverageBetaStore((s) => {
    // Deleted areas are excluded from submission, so they don't need to be valid.
    return values(s.idxToFeEmptyArea)
      .filter((feEmptyArea) => !feEmptyArea.isDeleted)
      .every(isEmptyAreaSubmittable);
  });

  // const completeJobTaskMutation = useMutation({
  //   ...mvpApiCompleteJobTaskMutation(),
  //   onSuccess: () => {
  //     console.log("success");
  //   },
  //   onError: () => {
  //     console.error("mvpApiCompleteJobTaskMutation");
  //   },
  // });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const {
        idxToFeEmptyArea,
        persons,
        basePlanogramIds,
        overridePlanogramIds,
        extraPlanogramIds,
        boxIdxToSlot,
        interactionCounts,
        eventCounts,
        confirmNudge,
        primaryImageId,
      } = useCoverageBetaStore.getState();

      // Blocked-action nudges live on their own counter; fold them into the
      // event counts as one more rework signal.
      const eventCountsWithNudge = confirmNudge
        ? { ...eventCounts, blocked_by_confirm: confirmNudge }
        : eventCounts;

      const result = buildCoverageBetaResult(
        idxToFeEmptyArea,
        persons,
        {
          basePlanogramIds,
          overridePlanogramIds,
          extraPlanogramIds,
          boxIdxToSlot,
        },
        { testing: job?.testing ?? false, primaryImageId },
      );

      // Fold in analytics: active/idle timing (viewer mode only; null in
      // admin/QA), hotkey-vs-click interaction counts, and prefetch validation.
      const timing = readCoverageBetaTiming();
      const resultWithMetadata = {
        ...result,
        metadata: {
          ...result.metadata,
          ...(timing ?? {}),
          ...(Object.keys(interactionCounts).length
            ? { interaction_counts: interactionCounts }
            : {}),
          ...(Object.keys(eventCountsWithNudge).length
            ? { event_counts: eventCountsWithNudge }
            : {}),
          prefetch_ready: isPrefetchReady,
          next_task_available: !!nextTaskId,
        },
      };

      const parsedResult = CompleteTaskPayloadSchema.safeParse({
        result: resultWithMetadata,
      });

      if (!parsedResult.success) {
        throw new Error("CompleteTaskPayloadSchema parsing error");
      }

      const body = parsedResult.data;

      const { error } = await mvpApiCompleteJobTask({
        body,
        path: { job_id, task_id },
      });
      // Stay on the task if it didn't save — unless it was already finalized
      // (double-click / retry), which is as good as saved.
      if (error && !isTaskAlreadyFinalized(error)) throw error;

      const nextPendingKey = mvpApiGetNextPendingJobTaskOptions({
        path: { job_id },
      }).queryKey;

      // Keep the prefetch's task-list selection fresh so the next "first
      // pending" target reflects this completion, not an already-seen task.
      queryClient.invalidateQueries({ queryKey: ["job-tasks", job_id] });

      if (isPrefetchReady && nextTaskId && nextTaskDetail) {
        // Optimistic flip: the prefetched task's parcel + images are already
        // warm, so the next task renders with no loading state. Then reconcile
        // in the background against the authoritative next-pending endpoint —
        // if the prefetch guessed wrong, the cache silently corrects.
        queryClient.setQueryData(nextPendingKey, nextTaskDetail);
        queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      } else {
        // No confident prefetch — refetch the next pending task (cold path).
        await queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Button
      size="sm"
      onClick={handleSubmit}
      loading={isSubmitting}
      disabled={!enableSubmit}
      // title={
      //   submitDisabled
      //     ? "Categorize all detections and select products before submitting"
      //     : undefined
      // }
    >
      Submit
    </Button>
  );
};
